import dayjs from 'dayjs';
import type { BuildHubTypeEnum } from 'generated/graphql';

/**
 * Simply return the scope label as-is since we now store display labels directly
 * @param scope - The scope label
 * @returns The scope label
 */
export const formatScopeLabel = (scope: string | null): string | null => {
    return scope;
};

/**
 * formatTypeLabel is a function that formats the type label.
 * It is used to display the type label in the build hub.
 *
 * @param type - The type to format.
 * @returns The formatted type label.
 */
export const formatTypeLabel = (type: BuildHubTypeEnum): string | null => {
    switch (type) {
        case 'Commercial':
            return 'Commercial';
        case 'Residential':
            return 'Residential';
        case 'Internal':
            return 'Internal';
        default:
            return null;
    }
};

/**
 * formatProjectDateLabel is a function that formats the project date label.
 * It is used to display the project date label in the build hub.
 *
 * This function treats the date as a date-only value (no time component)
 * and formats it without timezone conversion to avoid date shifting issues.
 *
 * @param date - The date to format (YYYY-MM-DD format)
 * @returns The formatted project date label.
 * @example
 * formatProjectDateLabel('2024-01-01') // returns 'January 2024'
 * formatProjectDateLabel(null) // returns 'As soon as possible'
 */
export const formatProjectDateLabel = (date: string | null): string => {
    if (!date) {
        return '-';
    }

    // Parse the date string components directly to avoid timezone conversion
    const datePart = date.split('T')[0];
    if (!datePart) return dayjs(date).format('MMMM YYYY');

    const parts = datePart.split('-');
    if (parts.length === 3 && parts[0] && parts[1] && parts[2]) {
        const year = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1; // JavaScript months are 0-indexed

        // Create a date object in the user's local timezone
        const localDate = dayjs(new Date(year, month, 1));
        return localDate.format('MMMM YYYY');
    }

    // Fallback to basic parsing if format is unexpected
    return dayjs(date).format('MMMM YYYY');
};

export const LOCATION_NOT_SPECIFIED = '-';
export const BUDGET_NOT_SPECIFIED = '-';
